import {
  BASE_NODE_URL,
  BASE_URL,
  ELASTIC_SEARCH_BASE_URL,
  USE_ELASTIC_FOR_PO_PRODUCT_LIST_V2,
  FORCE_NODE_API_FOR_STOCKTAKE_AND_PURCHASE_ORDER,
  CATEGORY_LIST,
  NEW_PURCHASE_ORDER_LIST,
  NEW_PURCHASE_ORDER_LIST_COUNT,
  NEW_PURCHASE_ORDER_COUNT,
  NEW_PURCHASE_ORDER_VENDOR_LIST,
  NEW_PURCHASE_ORDER_GENERATE_PO_NUMBER,
  NEW_PURCHASE_ORDER_ADD,
  NEW_PURCHASE_ORDER_ADD_AND_RECEIVE,
  NEW_PURCHASE_ORDER_ADD_FROM_CSV,
  NEW_PURCHASE_ORDER_DELETE,
  NEW_PURCHASE_ORDER_VOID,
  NEW_PURCHASE_ORDER_DETAILS,
  NEW_PURCHASE_ORDER_UPDATE,
  NEW_PURCHASE_ORDER_UPDATE_SOH,
  NEW_PURCHASE_ORDER_RECEIVE,
  NEW_PURCHASE_ORDER_EMAIL,
  NEW_PURCHASE_ORDER_PRODUCT_LIST,
  NEW_PURCHASE_ORDER_PRODUCT_BY_UPC,
  AUTO_PO_LIST,
  // ELASTIC_SEARCH_BASE_URL,
} from "../../Constants/Config";
import { ciUrl } from "../../Constants/nodeCi";
import {
  buildListPayload,
  buildDeletePurchaseOrderPayload,
  buildVoidPurchaseOrderPayload,
  buildPurchaseOrderDetailsPayload,
  buildUpdatePurchaseOrderPayload,
  buildUpdatePurchaseOrderItemsSohPayload,
  buildActivatePurchaseOrderPayload,
  buildReceivePurchaseOrderPayload,
  mapApiPurchaseOrder,
  parseCreatePurchaseOrderResponse,
  parseCsvImportErrorBody,
  parseDeletePurchaseOrderResponse,
  parseVoidPurchaseOrderResponse,
  parseUpdatePurchaseOrderResponse,
  parseReceivePurchaseOrderResponse,
  parseGeneratePoNumberResponse,
  parsePurchaseOrderDetailsResponse,
  parseSendPurchaseOrderEmailResponse,
  buildSendPurchaseOrderEmailPayload,
  resolveSearchByVendorParam,
} from "../../Components/NewPurchaseOrder/apiUtils";
import {
  PO_LIST_PER_PAGE,
  PO_PRODUCT_LIST_PER_PAGE,
  PO_VENDOR_LIST_PER_PAGE,
  PO_CATEGORY_LIST_PER_PAGE,
} from "../../Components/NewPurchaseOrder/constants";
import axios from "axios";
import { postNodeMultipart, toApiError } from "../http/postNodeMultipart";
import { sanitizeOrderNumber } from "../../Components/NewPurchaseOrder/createUtils";
import store from "../../Redux/app/store";
import { getSessionAuthHeader, withSessionFields } from "../../Common/sessionAuth";

/**
 * Base URL for the PO / stocktake product-list endpoints.
 *
 * Every product lookup on /purchase-order (list, add, edit/detail) and
 * /stocktake-app goes through here, so
 * FORCE_NODE_API_FOR_STOCKTAKE_AND_PURCHASE_ORDER is the single switch that
 * keeps those pages off elastic. Flip it to false in Constants/Config.js to
 * restore the previous routing: elastic when connected, Node as the fallback
 * (`isElasticConnected` is set at runtime by CheckElasticConnection, and is
 * false when elastic is off or unreachable).
 */
export function resolveProductListBaseUrl({ forceNodeApi = false } = {}) {
  if (forceNodeApi) return BASE_NODE_URL;
  if (FORCE_NODE_API_FOR_STOCKTAKE_AND_PURCHASE_ORDER) return BASE_NODE_URL;
  if (!USE_ELASTIC_FOR_PO_PRODUCT_LIST_V2) return BASE_NODE_URL;
  const isElasticConnected = store.getState()?.permissions?.isElasticConnected;
  return isElasticConnected ? ELASTIC_SEARCH_BASE_URL : BASE_NODE_URL;
}

/** `total_count` on list API is filter chip counts — not pagination metadata. */
const parseFilterTotalCount = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value;
};

/** PHP/Node sometimes returns list payloads as objects — normalize to a real array. */
const coerceToRowArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    return Object.values(value).filter(
      (entry) => entry && typeof entry === "object" && !Array.isArray(entry)
    );
  }
  return [];
};

/** Resolve the PO rows array from list API body (shape varies). */
const extractListRows = (responseData) => {
  if (!responseData) return [];

  const fromData = coerceToRowArray(responseData.data);
  if (fromData.length) return fromData;

  const fromResult = coerceToRowArray(responseData.result);
  if (fromResult.length) return fromResult;

  const nested = responseData.data;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    const nestedKeys = ["data", "list", "records", "rows", "purchase_orders"];
    for (const key of nestedKeys) {
      const rows = coerceToRowArray(nested[key]);
      if (rows.length) return rows;
    }
  }

  return [];
};

/**
 * Scroll pagination — API has no page/total/has_more.
 * Next page exists only when the current page returned a full `per_page` batch.
 */
const normalizeListResponse = (rawRows, responseData, requestPage, requestPerPage) => {
  const rows = coerceToRowArray(rawRows).filter(
    (row) => String(row?.is_deleted ?? "0") !== "1"
  );
  const perPage = requestPerPage ?? PO_LIST_PER_PAGE;
  const page = Number(requestPage) || 1;
  const hasMore = rows.length > 0 && rows.length >= perPage;

  return {
    items: rows.map((row, index) => mapApiPurchaseOrder(row, index)),
    totalCount: parseFilterTotalCount(responseData?.total_count),
    meta: {
      page,
      perPage,
      hasMore,
    },
  };
};

export async function fetchPurchaseOrdersList(payload, signal) {
  try {
    const response = await postNodeMultipart(
      BASE_NODE_URL + NEW_PURCHASE_ORDER_LIST,
      payload,
      signal
    );

    if (response?.data?.status) {
      const rows = extractListRows(response.data);
      return normalizeListResponse(
        rows,
        response.data,
        payload?.page,
        payload?.per_page
      );
    }

    return normalizeListResponse(
      [],
      response?.data,
      payload?.page,
      payload?.per_page
    );
  } catch (error) {
    throw toApiError(error);
  }
}

export async function fetchPurchaseOrdersCount(payload, signal) {
  try {
    const response = await postNodeMultipart(
      BASE_NODE_URL + NEW_PURCHASE_ORDER_COUNT,
      payload,
      signal
    );

    if (response?.data?.status && response?.data?.data) {
      return response.data.data;
    }

    return null;
  } catch (error) {
    throw toApiError(error);
  }
}

/** Tab badge counts — respects vendor filter and text search (not active status tab). */
export async function fetchPurchaseOrdersListCount(payload, signal) {
  try {
    const response = await postNodeMultipart(
      BASE_NODE_URL + NEW_PURCHASE_ORDER_LIST_COUNT,
      payload,
      signal
    );

    if (response?.data?.status && response?.data?.total_count) {
      return response.data.total_count;
    }

    return null;
  } catch (error) {
    throw toApiError(error);
  }
}

/** Build API payload from auth + normalized filters (used by query hooks). */
export function buildPurchaseOrdersListPayload(auth, filters, page = 1) {
  return buildListPayload({
    merchant_id: filters.merchantId,
    userTypeData: auth,
    searchText: filters.search,
    statusFilter: filters.status,
    sortCol: filters.sortCol,
    sortOrd: filters.sortOrd,
    vendorId: filters.vendorId,
    page,
    perPage: filters.perPage,
  });
}

export function buildPurchaseOrdersCountPayload(auth, merchantId) {
  return {
    ...auth,
    merchant_id: merchantId,
  };
}

export function buildPurchaseOrdersListCountPayload(
  auth,
  merchantId,
  vendorId = "all",
  search = ""
) {
  const trimmedSearch = String(search || "").trim();
  return {
    ...auth,
    merchant_id: merchantId,
    search_by: trimmedSearch ? trimmedSearch : "",
    search_by_vendor: resolveSearchByVendorParam(vendorId),
  };
}

const normalizeVendorListResponse = (responseData, requestPage, requestPerPage) => {
  const rows = coerceToRowArray(responseData?.data);
  const perPage = requestPerPage ?? PO_VENDOR_LIST_PER_PAGE;
  const page = Number(requestPage) || 1;
  const hasMore = rows.length > 0 && rows.length >= perPage;

  return {
    items: rows,
    meta: {
      page,
      perPage,
      hasMore,
    },
  };
};

export async function fetchNewPurchaseOrderVendorsList(payload, signal) {
  try {
    const response = await postNodeMultipart(
      BASE_NODE_URL + NEW_PURCHASE_ORDER_VENDOR_LIST,
      payload,
      signal
    );

    if (response?.data?.status) {
      return normalizeVendorListResponse(
        response.data,
        payload?.page,
        payload?.per_page
      );
    }

    return normalizeVendorListResponse(
      { data: response?.data?.data },
      payload?.page,
      payload?.per_page
    );
  } catch (error) {
    throw toApiError(error);
  }
}

export function buildNewPurchaseOrderVendorsPayload(
  auth,
  merchantId,
  {
    page = 1,
    perPage = PO_VENDOR_LIST_PER_PAGE,
    search = "",
    vendorId = "",
  } = {}
) {
  return {
    ...auth,
    merchant_id: merchantId,
    page: Number(page) || 1,
    per_page: perPage,
    search_by: String(search || "").trim(),
    search_by_vendor: vendorId ? String(vendorId) : "",
  };
}

/** Flat picker row — shared shape for variant lines and single-SKU products. */
const normalizeProductPickerRow = (product, variant) => ({
  product_id: String(product?.product_id || ""),
  varient_id:
    variant?.varient_id == null || String(variant.varient_id).trim() === ""
      ? null
      : String(variant.varient_id),
  product_name:
    variant?.product_name ||
    product?.product_title ||
    product?.product_name ||
    "",
  variant_name: variant?.variant_name || "",
  cost_price: variant?.cost_price ?? product?.cost_price ?? "0",
  // Selling price — coupon product picker prices/filters by this (not cost_price).
  price: variant?.price ?? product?.price ?? "0",
  quantity: variant?.quantity ?? product?.quantity ?? "0",
  upc: variant?.upc ?? product?.upc ?? "",
  custom_code: variant?.custom_code ?? product?.custom_code ?? "",
  is_lottery: String(variant?.is_lottery ?? product?.is_lottery ?? "0"),
  vendor_name:
    variant?.vendor_name ||
    product?.vendor_name ||
    product?.vendor ||
    product?.company_name ||
    "",
});

/**
 * v2 list item: product with nested `variants[]` (empty when no variants).
 * Produces one group per product for the picker modal.
 */
export const normalizeProductListProduct = (product) => {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const rowItems =
    variants.length > 0
      ? variants.map((variant) => normalizeProductPickerRow(product, variant))
      : [normalizeProductPickerRow(product, null)];

  const variantCount = Number(product?.variant_count);
  const resolvedVariantCount =
    Number.isFinite(variantCount) && variantCount > 0
      ? variantCount
      : variants.length > 0
        ? variants.length
        : 1;

  return {
    productId: String(product?.product_id || ""),
    productName: product?.product_title || product?.product_name || "Unnamed Product",
    vendorName: product?.vendor_name || "",
    hasVariants: variants.length > 0,
    variantCount: resolvedVariantCount,
    rowItems,
  };
};

export function buildNewPurchaseOrderProductListPayload(
  auth,
  {
    merchantId,
    vendorId = "",
    filterVendorId = "",
    categoryId = "",
    search,
    page,
    perPage = PO_PRODUCT_LIST_PER_PAGE,
    discAmt,
    // Deal pickers only (BOGO / Mix&Match / item coupon). Never set for PO.
    excludeDealProducts,
    currentDealType,
    currentDealId,
    // "stocked" on the PO / stocktake pickers: hides virtual kits (HQ parity).
    kitScope,
  }
) {
  const payload = {
    ...auth,
    merchant_id: merchantId,
    // Chosen supplier — backend floats its preferred-vendor products to the top
    // (full catalog is still returned; this sorts, it does not filter).
    vendor_id: String(vendorId || ""),
    // Vendor FILTER (assigned_vendors) + category FILTER — narrow the list.
    filter_vendor_id: String(filterVendorId || ""),
    category_id: String(categoryId || ""),
    search_by_product: search || "",
    page: Number(page) || 1,
    per_page: perPage,
  };
  // Discount Amount filter (`disc_amt`) — used by item-coupon / BOGO / Mix & Match
  // amount pickers. Omitted for PO / stocktake so those lists stay unfiltered.
  if (discAmt !== undefined && discAmt !== null) {
    payload.disc_amt = String(discAmt);
  }
  // Deal create/edit product pickers — hide products already in other deals.
  // PO / stocktake omit these entirely.
  if (excludeDealProducts !== undefined && excludeDealProducts !== null) {
    payload.exclude_deal_products = String(excludeDealProducts);
  }
  if (currentDealType) {
    payload.current_deal_type = String(currentDealType);
  }
  if (currentDealId !== undefined && currentDealId !== null && currentDealId !== "") {
    payload.current_deal_id = String(currentDealId);
  }
  if (kitScope) {
    payload.kit_scope = String(kitScope);
  }
  return payload;
}

/** Category list for the product-picker filter (PHP Categoryapi/category_list). */
const normalizeCategoryListResponse = (rows, requestPage, requestPerPage) => {
  const list = Array.isArray(rows) ? rows : [];
  const perPage = requestPerPage ?? PO_CATEGORY_LIST_PER_PAGE;
  const page = Number(requestPage) || 1;
  const hasMore = list.length > 0 && list.length >= perPage;

  return {
    items: list
      .map((row) => ({
        id: String(row?.id ?? ""),
        name: row?.title || row?.alternateName || row?.name || "Untitled",
      }))
      .filter((c) => c.id),
    meta: {
      page,
      perPage,
      hasMore,
    },
  };
};

export async function fetchPurchaseOrderCategories(
  { merchantId, page = 1, perPage = PO_CATEGORY_LIST_PER_PAGE },
  signal
) {
  try {
    const formData = new FormData();
    formData.append("merchant_id", merchantId || "");
    formData.append("page", String(Number(page) || 1));
    formData.append("per_page", String(perPage));
    // Callers pass no auth, so read the session here like the sibling calls do.
    const response = await axios.post(ciUrl(CATEGORY_LIST), withSessionFields(formData), {
      signal,
      headers: getSessionAuthHeader(),
    });
    const rows = response?.data?.status ? response.data.result : [];
    return normalizeCategoryListResponse(rows, page, perPage);
  } catch (error) {
    throw toApiError(error);
  }
}

export async function fetchNewPurchaseOrderProductList(
  payload,
  signal,
  { forceNodeApi = false } = {}
) {
  try {
    const response = await postNodeMultipart(
      resolveProductListBaseUrl({ forceNodeApi }) + NEW_PURCHASE_ORDER_PRODUCT_LIST,
      payload,
      signal
    );
    // v2 returns HTTP 200 with status:false + a message when the search matches
    // too many products (e.g. "Search matched too many products (9222). Please
    // refine your search."). Surface that message; there are no rows to render.
    if (response?.data?.status === false) {
      return {
        products: [],
        meta: {
          page: Number(payload.page) || 1,
          perPage: payload.per_page || PO_PRODUCT_LIST_PER_PAGE,
          total: 0,
          hasMore: false,
          message: response?.data?.message || "",
        },
      };
    }
    const apiProducts = coerceToRowArray(response?.data?.data);
    const products = apiProducts.map(normalizeProductListProduct);

    const pagination = response?.data?.pagination;
    const page = Number(pagination?.page) || Number(payload.page) || 1;
    const perPage =
      Number(pagination?.per_page) || payload.per_page || PO_PRODUCT_LIST_PER_PAGE;
    const total = Number(pagination?.total);
    const hasMore = Number.isFinite(total)
      ? page * perPage < total
      : products.length >= perPage;

    return {
      products,
      meta: {
        page,
        perPage,
        total: Number.isFinite(total) ? total : undefined,
        hasMore,
      },
    };
  } catch (error) {
    throw toApiError(error);
  }
}

/** Build payload for the fast exact-UPC scan lookup. */
export function buildNewPurchaseOrderProductByUpcPayload(auth, { merchantId, upc, kitScope }) {
  return {
    ...auth,
    merchant_id: merchantId,
    upc: String(upc || "").trim(),
    ...(kitScope ? { kit_scope: String(kitScope) } : {}),
  };
}

/** Resolve ONE product/variant row by exact UPC (scan fast-path). Null if no match. */
export async function fetchNewPurchaseOrderProductByUpc(
  payload,
  signal,
  { forceNodeApi = false } = {}
) {
  try {
    const response = await postNodeMultipart(
      resolveProductListBaseUrl({ forceNodeApi }) + NEW_PURCHASE_ORDER_PRODUCT_BY_UPC,
      payload,
      signal
    );
    if (response?.data?.status && response?.data?.data) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    throw toApiError(error);
  }
}

export function buildGeneratePoNumberPayload(auth, merchantId, poNumber) {
  const payload = {
    ...auth,
    merchant_id: merchantId,
  };
  const trimmed = sanitizeOrderNumber(poNumber).trim();
  if (trimmed) {
    payload.po_number = trimmed;
  }
  return payload;
}

export async function fetchGeneratePoNumber(payload, signal, options = {}) {
  try {
    const response = await postNodeMultipart(
      BASE_NODE_URL + NEW_PURCHASE_ORDER_GENERATE_PO_NUMBER,
      payload,
      signal
    );
    const parsed = parseGeneratePoNumberResponse(response?.data, options);
    if (!parsed.ok && !options.checkingDuplicate) {
      throw toApiError({
        message: parsed.message || "Failed to generate PO number",
        data: response?.data,
      });
    }
    return parsed;
  } catch (error) {
    // Legacy duplicate-check responses used HTTP 400; parse body when present.
    if (options.checkingDuplicate) {
      const responseData = error?.response?.data ?? error?.data;
      if (responseData && typeof responseData === "object") {
        return parseGeneratePoNumberResponse(responseData, options);
      }
    }
    if (error?.message && error?.data !== undefined) throw error;
    throw toApiError(error);
  }
}

export function buildNewPurchaseOrderDetailsPayload(auth, merchantId, poId, storeIds) {
  return buildPurchaseOrderDetailsPayload({ auth, merchantId, poId, storeIds });
}

export async function fetchNewPurchaseOrderDetails(payload, signal) {
  try {
    const response = await postNodeMultipart(
      BASE_NODE_URL + NEW_PURCHASE_ORDER_DETAILS,
      payload,
      signal
    );
    const parsed = parsePurchaseOrderDetailsResponse(response?.data);
    if (!parsed.ok) {
      throw toApiError({
        message: parsed.message || "Failed to load purchase order",
        data: response?.data,
      });
    }
    return parsed.detail;
  } catch (error) {
    const responseData = error?.response?.data;
    if (responseData && typeof responseData === "object") {
      const parsed = parsePurchaseOrderDetailsResponse(responseData);
      if (parsed.ok && parsed.noProducts) {
        return parsed.detail;
      }
    }
    if (error?.message && error?.data !== undefined) throw error;
    throw toApiError(error);
  }
}

export function buildDeleteNewPurchaseOrderPayload(
  auth,
  merchantId,
  poId,
  employeeId
) {
  return buildDeletePurchaseOrderPayload({
    auth,
    merchantId,
    poId,
    employeeId,
  });
}

export async function deleteNewPurchaseOrder(payload, signal) {
  try {
    const response = await postNodeMultipart(
      BASE_NODE_URL + NEW_PURCHASE_ORDER_DELETE,
      payload,
      signal
    );
    const parsed = parseDeletePurchaseOrderResponse(response?.data);
    if (!parsed.ok) {
      throw toApiError({
        message: parsed.message || "Failed to delete purchase order",
        data: response?.data,
      });
    }
    return parsed;
  } catch (error) {
    if (error?.message && error?.data !== undefined) throw error;
    throw toApiError(error);
  }
}

export function buildVoidNewPurchaseOrderPayload(
  auth,
  merchantId,
  poId,
  employeeId
) {
  return buildVoidPurchaseOrderPayload({
    auth,
    merchantId,
    poId,
    employeeId,
  });
}

export async function voidNewPurchaseOrder(payload, signal) {
  try {
    const response = await postNodeMultipart(
      BASE_NODE_URL + NEW_PURCHASE_ORDER_VOID,
      payload,
      signal
    );
    const parsed = parseVoidPurchaseOrderResponse(response?.data);
    if (!parsed.ok) {
      throw toApiError({
        message: parsed.message || "Failed to cancel purchase order",
        data: response?.data,
      });
    }
    return parsed;
  } catch (error) {
    if (error?.message && error?.data !== undefined) throw error;
    throw toApiError(error);
  }
}

export function buildUpdateNewPurchaseOrderPayload({
  auth,
  merchantId,
  employeeId,
  employeeName,
  po,
  apiPoId,
  expectedAt,
  reference,
  note,
  deletedItemIds,
  isDraft,
  receivedStatus,
}) {
  return buildUpdatePurchaseOrderPayload({
    auth,
    merchantId,
    employeeId,
    employeeName,
    po,
    apiPoId,
    expectedAt,
    reference,
    note,
    deletedItemIds,
    isDraft,
    receivedStatus,
  });
}

export function buildActivateNewPurchaseOrderPayload(params) {
  return buildActivatePurchaseOrderPayload(params);
}

export async function updateNewPurchaseOrder(payload, signal) {
  try {
    const response = await postNodeMultipart(
      BASE_NODE_URL + NEW_PURCHASE_ORDER_UPDATE,
      payload,
      signal
    );
    const parsed = parseUpdatePurchaseOrderResponse(response?.data);
    if (!parsed.ok) {
      throw toApiError({
        message: parsed.message || "Failed to update purchase order",
        data: response?.data,
      });
    }
    return parsed;
  } catch (error) {
    if (error?.message && error?.data !== undefined) throw error;
    throw toApiError(error);
  }
}

export function buildUpdateNewPurchaseOrderItemsSohPayload(params) {
  return buildUpdatePurchaseOrderItemsSohPayload(params);
}

/** Stock on Hand → existing PO. Does not fetch details or rewrite the PO header. */
export async function updateNewPurchaseOrderItemsSoh(payload, signal) {
  try {
    const response = await postNodeMultipart(
      BASE_NODE_URL + NEW_PURCHASE_ORDER_UPDATE_SOH,
      payload,
      signal
    );
    const parsed = parseUpdatePurchaseOrderResponse(response?.data);
    if (!parsed.ok) {
      throw toApiError({
        message: parsed.message || "Failed to update purchase order",
        data: response?.data,
      });
    }
    return parsed;
  } catch (error) {
    const responseData = error?.response?.data;
    if (responseData && typeof responseData === "object") {
      const parsed = parseUpdatePurchaseOrderResponse(responseData);
      throw toApiError({
        message: parsed.message || "Failed to update purchase order",
        data: responseData,
      });
    }
    if (error?.message && error?.data !== undefined) throw error;
    throw toApiError(error);
  }
}

export function buildReceiveNewPurchaseOrderPayload({
  auth,
  merchantId,
  employeeId,
  employeeName,
  po,
  receiveItems,
  receiveNote,
}) {
  return buildReceivePurchaseOrderPayload({
    auth,
    merchantId,
    employeeId,
    employeeName,
    po,
    receiveItems,
    receiveNote,
  });
}

export async function receiveNewPurchaseOrder(payload, signal) {
  try {
    const response = await postNodeMultipart(
      BASE_NODE_URL + NEW_PURCHASE_ORDER_RECEIVE,
      payload,
      signal
    );
    const parsed = parseReceivePurchaseOrderResponse(response?.data);
    if (!parsed.ok) {
      throw toApiError({
        message: parsed.message || "Failed to receive purchase order items",
        data: response?.data,
      });
    }
    return parsed;
  } catch (error) {
    if (error?.message && error?.data !== undefined) throw error;
    throw toApiError(error);
  }
}

export async function createNewPurchaseOrder(payload, signal) {
  try {
    const response = await postNodeMultipart(
      BASE_NODE_URL + NEW_PURCHASE_ORDER_ADD,
      payload,
      signal
    );
    // console.log("response createNewPurchaseOrder", response);
    const parsed = parseCreatePurchaseOrderResponse(response?.data);
    if (!parsed.ok) {
      throw toApiError({
        message: parsed.message || "Failed to save purchase order",
        data: response?.data,
      });
    }
    return parsed;
  } catch (error) {
    if (error?.message && error?.data !== undefined) throw error;
    throw toApiError(error);
  }
}

/** Create a PO and receive every line in full, in one backend transaction. */
export async function createAndReceiveNewPurchaseOrder(payload, signal) {
  try {
    const response = await postNodeMultipart(
      BASE_NODE_URL + NEW_PURCHASE_ORDER_ADD_AND_RECEIVE,
      payload,
      signal
    );
    const parsed = parseCreatePurchaseOrderResponse(response?.data);
    if (!parsed.ok) {
      throw toApiError({
        message: parsed.message || "Failed to create and receive purchase order",
        data: response?.data,
      });
    }
    return parsed;
  } catch (error) {
    if (error?.message && error?.data !== undefined) throw error;
    throw toApiError(error);
  }
}

export async function createNewPurchaseOrderFromCsv(payload, signal) {
  try {
    const { token, csv_file: csvFile, ...fields } = payload;
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });
    if (csvFile) {
      formData.append("csv_file", csvFile);
    }

    const response = await axios.post(
      BASE_NODE_URL + NEW_PURCHASE_ORDER_ADD_FROM_CSV,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      }
    );

    const parsed = parseCreatePurchaseOrderResponse(response?.data);
    if (!parsed.ok) {
      const { message, skippedUpcs, skippedProducts } = parseCsvImportErrorBody(
        response?.data
      );
      throw {
        message: message || parsed.message || "Failed to save purchase order from CSV",
        data: response?.data,
        skippedUpcs,
        skippedProducts,
        isCsvImportError: true,
      };
    }
    return parsed;
  } catch (error) {
    if (error?.isCsvImportError) throw error;
    if (error?.message && error?.data !== undefined) {
      const { skippedUpcs } = parseCsvImportErrorBody(error.data);
      if (skippedUpcs.length) {
        throw { ...error, skippedUpcs, isCsvImportError: true };
      }
      throw error;
    }
    const apiError = toApiError(error);
    const { message, skippedUpcs } = parseCsvImportErrorBody(apiError.data);
    if (message || skippedUpcs.length) {
      throw {
        ...apiError,
        message: message || apiError.message,
        skippedUpcs,
        isCsvImportError: true,
      };
    }
    throw apiError;
  }
}

export function buildSendNewPurchaseOrderEmailPayload(params) {
  return buildSendPurchaseOrderEmailPayload(params);
}

export async function sendNewPurchaseOrderEmail(payload, signal) {
  try {
    const { token, attachment, ...fields } = payload;
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        formData.append(key, value);
      }
    });
    if (attachment) {
      formData.append("attachment", attachment);
    }

    const response = await axios.post(
      BASE_NODE_URL + NEW_PURCHASE_ORDER_EMAIL,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      }
    );

    const parsed = parseSendPurchaseOrderEmailResponse(response?.data);
    if (!parsed.ok) {
      throw toApiError({
        message: parsed.message || "Failed to send purchase order email",
        data: response?.data,
      });
    }
    return parsed;
  } catch (error) {
    if (error?.message && error?.data !== undefined) throw error;
    throw toApiError(error);
  }
}

/** Legacy PHP API — vendor-assigned products for Auto PO (create flow). */
export async function fetchAutoPoList(
  { merchantId, vendorId, auth },
  signal
) {
  try {
    const formData = new FormData();
    formData.append("merchant_id", merchantId || "");
    formData.append("admin_id", merchantId || "");
    formData.append(
      "login_employee_id",
      auth?.login_employee_id ? String(auth.login_employee_id) : null
    );
    formData.append("login_type", auth?.login_type || "");
    formData.append("token_id", auth?.token_id || "");
    formData.append("vendor_id", String(Number(vendorId) || vendorId || ""));

    const response = await axios.post(BASE_URL + AUTO_PO_LIST, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${auth?.token || ""}`,
      },
      signal,
    });

    if (!response?.data?.status) {
      throw toApiError({
        message: response?.data?.message || "Failed to fetch auto PO list",
        data: response?.data,
      });
    }

    return coerceToRowArray(response.data.result);
  } catch (error) {
    if (error?.message && error?.data !== undefined) throw error;
    throw toApiError(error);
  }
}

